/**
 * validateRrule.ts
 * ---------------------------------------------------------------------------
 * Shared RRULE validator used by:
 * - Graph-service write routes (addStatusFactor, updateStatusFactor) to reject
 *   malformed schedules at write time rather than storing them and having
 *   materialization silently expand to zero occurrences later.
 * - SchedulesEditorMolecule (design-system) so client-side and server-side
 *   validation cannot drift.
 *
 * Anything callers want to enforce on a custom RRULE goes through this file
 * exactly once. Duplicating the checks elsewhere is a bug.
 */

import { RRule } from 'rrule';

/**
 * A field-level validation error. `field` matches the schedule shape the
 * client editor exposes so an error can be attached to the offending control
 * (or to `rrule` / `dtstart` / `timezone` as a fallback).
 */
export interface RruleValidationError {
  field: 'rrule' | 'dtstart' | 'until' | 'count' | 'interval' | 'freq' | 'timezone';
  code:
    | 'parse_failed'
    | 'freq_missing'
    | 'freq_unsupported'
    | 'interval_below_one'
    | 'count_below_one'
    | 'until_before_dtstart'
    | 'timezone_unresolvable';
  message: string;
}

export type RruleValidationResult =
  | { ok: true }
  | { ok: false; errors: RruleValidationError[] };

/** RFC-5545 frequencies we materialize schedules for. */
const SUPPORTED_FREQ_INDEXES = new Set<number>([
  0, // YEARLY
  1, // MONTHLY
  2, // WEEKLY
  3, // DAILY
]);

const FREQ_NAMES = ['YEARLY', 'MONTHLY', 'WEEKLY', 'DAILY', 'HOURLY', 'MINUTELY', 'SECONDLY'];

/** Normalize a Date | string | null | undefined into Date | null. */
function toDate(value: Date | string | null | undefined): Date | null {
  if (value instanceof Date) return value;
  if (value) return new Date(value);
  return null;
}

export interface ValidateRruleInput {
  /**
   * The RRULE string as stored on the schedule. May be either the bare rule
   * body (`FREQ=WEEKLY;BYDAY=MO`) or the `RRULE:` prefixed form. Both are
   * accepted; we normalize before parsing.
   */
  rrule?: string | null;
  /** Optional DTSTART as an ISO 8601 string. */
  dtstart?: string | null;
  /** Optional IANA timezone name (e.g. `America/Toronto`). */
  timezone?: string | null;
}

/**
 * Validate a custom RRULE + its dtstart / timezone context.
 *
 * The intent is that a schedule that passes validation here will expand to
 * at least one occurrence given a plausible date range — so we catch:
 *
 * - Malformed rules (unparseable via `RRule.parseString`).
 * - `FREQ` missing or set to a value we don't materialize (HOURLY/MINUTELY/SECONDLY).
 * - `INTERVAL < 1`. Zero or negative intervals produce an infinite/undefined loop.
 * - `COUNT < 1`. Zero occurrences up front is not a schedule, it's a mistake.
 * - `UNTIL <= DTSTART`. Same problem: nothing ever fires.
 * - Timezone that `Intl.DateTimeFormat` refuses to accept.
 */
export function validateRrule(input: ValidateRruleInput): RruleValidationResult {
  const errors: RruleValidationError[] = [];

  // A missing rrule isn't a validation error at this layer — recurring
  // schedules must have one, but the caller decides whether the rrule is
  // required based on scheduleSource. Empty input passes trivially.
  if (!input.rrule || input.rrule.trim() === '') {
    return { ok: true };
  }

  // Timezone check is independent of the rrule content — a broken tz means
  // the schedule cannot be materialized even if the rrule itself is fine.
  if (input.timezone && input.timezone !== 'Local time') {
    try {
      // Intl throws a RangeError for unrecognized IANA names.
      new Intl.DateTimeFormat('en-US', { timeZone: input.timezone });
    } catch {
      errors.push({
        field: 'timezone',
        code: 'timezone_unresolvable',
        message: `Timezone "${input.timezone}" is not a valid IANA name.`,
      });
    }
  }

  // Normalize away the RRULE: prefix and any embedded DTSTART lines so the
  // parser sees a clean rule body. rrule's parseString throws on either.
  const cleaned = input.rrule
    .replace(/^RRULE:/i, '')
    .replace(/DTSTART[^;]*;?/gi, '')
    .trim();

  let options: Partial<import('rrule').Options>;
  try {
    options = RRule.parseString(cleaned);
  } catch (err) {
    errors.push({
      field: 'rrule',
      code: 'parse_failed',
      message: err instanceof Error ? err.message : 'RRULE could not be parsed.',
    });
    // If the rule itself won't parse, downstream field checks would either
    // duplicate the parse error or fire on garbage — return early.
    return { ok: false, errors };
  }

  if (options.freq === undefined || options.freq === null) {
    errors.push({
      field: 'freq',
      code: 'freq_missing',
      message: 'RRULE must include a FREQ.',
    });
  } else if (!SUPPORTED_FREQ_INDEXES.has(options.freq)) {
    const name = FREQ_NAMES[options.freq] ?? String(options.freq);
    errors.push({
      field: 'freq',
      code: 'freq_unsupported',
      message: `FREQ=${name} is not supported. Use YEARLY, MONTHLY, WEEKLY, or DAILY.`,
    });
  }

  if (options.interval !== undefined && options.interval !== null && options.interval < 1) {
    errors.push({
      field: 'interval',
      code: 'interval_below_one',
      message: 'INTERVAL must be at least 1.',
    });
  }

  if (options.count !== undefined && options.count !== null && options.count < 1) {
    errors.push({
      field: 'count',
      code: 'count_below_one',
      message: 'COUNT must be at least 1.',
    });
  }

  // UNTIL vs DTSTART. Two paths: UNTIL is inside the rrule (options.until),
  // and DTSTART might be either inside the rrule (options.dtstart, populated
  // when the rule string carried DTSTART: — cleaned above, so unlikely here)
  // or on the sidecar `input.dtstart`.
  const untilDate = toDate(options.until);
  // Prefer the rrule-parsed dtstart (already a Date instance); otherwise fall
  // back to the sidecar `input.dtstart`. Anything non-Date on `options.dtstart`
  // is ignored to preserve the original semantics.
  const dtstartDate =
    options.dtstart instanceof Date ? options.dtstart : toDate(input.dtstart);
  if (untilDate && dtstartDate) {
    if (!Number.isNaN(untilDate.getTime()) && !Number.isNaN(dtstartDate.getTime())) {
      if (untilDate.getTime() <= dtstartDate.getTime()) {
        errors.push({
          field: 'until',
          code: 'until_before_dtstart',
          message: 'UNTIL must be after DTSTART.',
        });
      }
    }
  }

  return errors.length > 0 ? { ok: false, errors } : { ok: true };
}
